/**
 * EDIT PROFILE SCREEN
 *
 * Full account editing for drivers: name, phone, email, base address, working radius.
 * Includes address autocomplete and radius map preview.
 */
import React, { useState, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  TextInput, Platform, Alert, ActivityIndicator, KeyboardAvoidingView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Slider from '@react-native-community/slider';
import { useDispatch } from 'react-redux';
import { colors, spacing, fonts, RD } from '../../theme/colors';
import { api } from '../../services/api';
import { setDriver } from '../../store';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { RadiusMap } from '../../components/RadiusMap';
import { Toast } from '../../components/Toast';

// ─── Phone Formatter ───────────────────────────────────────────────────────────

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function stripPhone(formatted: string): string {
  return formatted.replace(/\D/g, '');
}

// ─── Screen ────────────────────────────────────────────────────────────────────

export function EditProfileScreen({ navigation, route }: any) {
  const dispatch = useDispatch();
  const driver = route.params?.driver;

  const [name, setName] = useState(driver?.name || '');
  const [phone, setPhone] = useState(formatPhone(driver?.phone || ''));
  const [email, setEmail] = useState(driver?.email || '');
  const [address, setAddress] = useState(driver?.baseAddress || '');
  const [lat, setLat] = useState(driver?.baseLat || 40.7128);
  const [lng, setLng] = useState(driver?.baseLng || -74.006);
  // Seed from the API column FIRST (`workingRadius`), then the alias. Seeding only from the
  // alias meant this slider always started at 200, so saving any unrelated edit posted 200
  // and silently clamped the driver's real radius down to the server's 300-mile floor.
  const [radius, setRadius] = useState(driver?.workingRadius ?? driver?.workingRadiusMiles ?? 200);
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [vehicleType, setVehicleType] = useState(driver?.vehicleType || '');
  const [vehicleDetails, setVehicleDetails] = useState(driver?.vehicleDetails || '');
  const [insuranceProvider, setInsuranceProvider] = useState(driver?.insuranceProvider || '');

  const handleAddressSelect = useCallback((addr: string, newLat: number, newLng: number) => {
    setAddress(addr);
    setLat(newLat);
    setLng(newLng);
  }, []);

  const handlePhoneChange = useCallback((text: string) => {
    setPhone(formatPhone(text));
  }, []);

  const handleSave = async () => {
    if (!name.trim()) {
      Alert.alert('Required', 'Please enter your full name.');
      return;
    }
    if (stripPhone(phone).length < 10) {
      Alert.alert('Required', 'Please enter a valid 10-digit phone number.');
      return;
    }
    if (!email.trim() || !email.includes('@')) {
      Alert.alert('Required', 'Please enter a valid email address.');
      return;
    }

    const payload = {
      name: name.trim(),
      phone: stripPhone(phone),
      email: email.trim().toLowerCase(),
      baseAddress: address,
      baseLat: lat,
      baseLng: lng,
      workingRadiusMiles: radius,
      vehicleType: vehicleType.trim(),
      vehicleDetails: vehicleDetails.trim(),
      insuranceProvider: insuranceProvider.trim(),
    };

    setSaving(true);
    let res;
    try {
      res = await api.put('/drivers/me', payload);
    } catch {
      // Do NOT pretend it saved — surface the failure and keep the driver on this screen
      // with their edits intact so they can retry. (Previously this swallowed the error and
      // showed "Changes Saved", then the local copy silently reverted on the next refetch.)
      setSaving(false);
      Alert.alert('Could not save', 'We couldn’t reach the server. Please check your connection and tap Save again.');
      return;
    }

    // Merge onto the EXISTING driver so we never drop fields the form doesn't edit
    // (id, accountNumber, isFleetManager, fleetManagerId…). Prefer the server's echo.
    dispatch(setDriver({
      ...(driver || {}),
      ...(res?.data && res.data.name ? res.data : {
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
        baseLat: lat,
        baseLng: lng,
        baseAddress: address,
        vehicleType: payload.vehicleType,
        vehicleDetails: payload.vehicleDetails,
        insuranceProvider: payload.insuranceProvider,
        workingRadiusMiles: radius,
      }),
    }));
    setSaving(false);
    setShowToast(true);
  };

  return (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            style={styles.backBtn}
            activeOpacity={0.6}
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          >
            <Ionicons name="chevron-back" size={24} color={RD.text} />
          </TouchableOpacity>
          <View style={styles.headerCenter}>
            <Text style={styles.headerTitle}>Edit Profile</Text>
            <Text style={styles.headerSub}>Keep your details up to date</Text>
          </View>
          <View style={styles.headerSpacer} />
        </View>

        {/* Profile Photo */}
        <View style={styles.photoCard}>
          <View style={styles.avatarWrap}>
            <View style={styles.avatar}>
              <Ionicons name="person" size={38} color={RD.maroon} />
            </View>
            <View style={styles.avatarBadge}>
              <Ionicons name="camera" size={14} color="#fff" />
            </View>
          </View>
          <Text style={styles.changePhoto}>Change photo</Text>
        </View>

        {/* Personal Information */}
        <View style={styles.sectionCard}>
          <View style={styles.sectionHeader}>
            <Ionicons name="person-outline" size={18} color={RD.maroon} />
            <Text style={styles.sectionTitle}>Personal Information</Text>
          </View>

          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Full Name</Text>
            <View style={styles.inputWrap}>
              <Ionicons name="person-outline" size={16} color={RD.textFaint} style={styles.inputIcon} />
              <TextInput
                style={styles.input}
                value={name}
                onChangeText={setName}
                placeholder="John Doe"
                placeholderTextColor={RD.g30}
                autoCapitalize="words"
                autoCorrect={false}
              />
            </View>
          </View>

          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Phone Number</Text>
            <View style={styles.inputWrap}>
              <Ionicons name="call-outline" size={16} color={RD.textFaint} style={styles.inputIcon} />
              <TextInput
                style={styles.input}
                value={phone}
                onChangeText={handlePhoneChange}
                placeholder="(555) 000-1234"
                placeholderTextColor={RD.g30}
                keyboardType="phone-pad"
                maxLength={14}
              />
            </View>
          </View>

          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Email Address</Text>
            <View style={styles.inputWrap}>
              <Ionicons name="mail-outline" size={16} color={RD.textFaint} style={styles.inputIcon} />
              <TextInput
                style={styles.input}
                value={email}
                onChangeText={setEmail}
                placeholder="driver@example.com"
                placeholderTextColor={RD.g30}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
              />
            </View>
          </View>
        </View>

        {/* Vehicle Information */}
        <View style={styles.sectionCard}>
          <View style={styles.sectionHeader}>
            <Ionicons name="car-outline" size={18} color={RD.maroon} />
            <Text style={styles.sectionTitle}>Vehicle Information</Text>
          </View>

          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Vehicle Type</Text>
            <View style={styles.inputWrap}>
              <Ionicons name="options-outline" size={16} color={RD.textFaint} style={styles.inputIcon} />
              <TextInput
                style={styles.input}
                value={vehicleType}
                onChangeText={setVehicleType}
                placeholder="e.g., Box Truck, Cargo Van"
                placeholderTextColor={RD.g30}
                autoCapitalize="words"
              />
            </View>
          </View>

          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Vehicle Make / Model</Text>
            <View style={styles.inputWrap}>
              <Ionicons name="car-sport-outline" size={16} color={RD.textFaint} style={styles.inputIcon} />
              <TextInput
                style={styles.input}
                value={vehicleDetails}
                onChangeText={setVehicleDetails}
                placeholder="e.g., 2022 Ford Transit"
                placeholderTextColor={RD.g30}
                autoCapitalize="words"
              />
            </View>
          </View>

          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Insurance Provider</Text>
            <View style={styles.inputWrap}>
              <Ionicons name="shield-checkmark-outline" size={16} color={RD.textFaint} style={styles.inputIcon} />
              <TextInput
                style={styles.input}
                value={insuranceProvider}
                onChangeText={setInsuranceProvider}
                placeholder="e.g., Progressive, State Farm"
                placeholderTextColor={RD.g30}
                autoCapitalize="words"
              />
            </View>
          </View>
        </View>

        {/* Base Address */}
        <View style={[styles.sectionCard, { zIndex: 10 }]}>
          <View style={styles.sectionHeader}>
            <Ionicons name="location-outline" size={18} color={RD.maroon} />
            <Text style={styles.sectionTitle}>Base Address</Text>
          </View>

          <Text style={styles.helperText}>
            Jobs will be matched based on proximity to your base address.
          </Text>

          <AddressAutocomplete
            value={address}
            onSelect={handleAddressSelect}
            placeholder="Enter your home address..."
          />
        </View>

        {/* Working Radius */}
        <View style={styles.sectionCard}>
          <View style={styles.sectionHeader}>
            <Ionicons name="resize-outline" size={18} color={RD.maroon} />
            <Text style={styles.sectionTitle}>Working Radius</Text>
          </View>

          <Text style={styles.helperText}>
            You will only see jobs within this distance from your base address.
          </Text>

          <View style={styles.sliderHeader}>
            <Text style={styles.sliderValue}>{radius} miles</Text>
          </View>

          <Slider
            style={styles.slider}
            minimumValue={10}
            maximumValue={1000}
            step={10}
            value={radius}
            onValueChange={setRadius}
            minimumTrackTintColor={RD.maroon}
            maximumTrackTintColor={RD.g10}
            thumbTintColor={RD.maroon}
          />

          <View style={styles.sliderLabels}>
            <Text style={styles.sliderLabel}>10 mi</Text>
            <Text style={styles.sliderLabel}>1,000 mi</Text>
          </View>

          {/* Radius Map */}
          <View style={styles.mapContainer}>
            <RadiusMap latitude={lat} longitude={lng} radiusMiles={radius} height={220} />
          </View>
        </View>

        {/* Save Button */}
        <TouchableOpacity
          style={[styles.saveBtn, saving && styles.saveBtnDisabled]}
          onPress={handleSave}
          activeOpacity={0.8}
          disabled={saving}
        >
          {saving ? (
            <ActivityIndicator size="small" color={colors.white} />
          ) : (
            <>
              <Ionicons name="checkmark-circle-outline" size={20} color={colors.white} />
              <Text style={styles.saveBtnText}>Save Changes</Text>
            </>
          )}
        </TouchableOpacity>

        <View style={styles.bottomSpacer} />
      </ScrollView>

      <Toast
        visible={showToast}
        message="Changes Saved"
        type="success"
        onDismiss={() => {
          setShowToast(false);
          navigation.goBack();
        }}
        duration={2000}
      />
    </KeyboardAvoidingView>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  flex:             { flex: 1, backgroundColor: RD.bg },
  container:        { flex: 1, backgroundColor: RD.bg },
  content:          { padding: spacing.lg, paddingTop: Platform.OS === 'ios' ? 60 : 40, paddingBottom: 40 },

  // Header
  header:           { flexDirection: 'row', alignItems: 'center', marginBottom: spacing.lg },
  backBtn:          { width: 40, height: 40, borderRadius: 20, backgroundColor: RD.surface, justifyContent: 'center', alignItems: 'center', borderWidth: 1, borderColor: RD.border, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  headerCenter:     { flex: 1, alignItems: 'center' },
  headerTitle:      { fontSize: 19, fontWeight: '700', color: RD.text, fontFamily: fonts.heavy },
  headerSub:        { fontSize: 12, color: RD.textFaint, marginTop: 2 },
  headerSpacer:     { width: 40 },

  // Photo card
  photoCard:        { backgroundColor: RD.surface, borderRadius: 18, padding: spacing.lg, marginBottom: spacing.md, alignItems: 'center', gap: 10, borderWidth: 1, borderColor: RD.border, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  avatarWrap:       { position: 'relative' },
  avatar:           { width: 76, height: 76, borderRadius: 38, backgroundColor: RD.maroonSoft, justifyContent: 'center', alignItems: 'center' },
  avatarBadge:      { position: 'absolute', bottom: -2, right: -2, width: 28, height: 28, borderRadius: 14, backgroundColor: RD.ink, borderWidth: 2, borderColor: '#fff', justifyContent: 'center', alignItems: 'center' },
  changePhoto:      { fontSize: 13, fontWeight: '600', color: RD.maroon },

  // Section cards
  sectionCard:      { backgroundColor: RD.surface, borderRadius: 18, padding: 16, marginBottom: spacing.md, borderWidth: 1, borderColor: RD.border, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  sectionHeader:    { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: spacing.md, paddingBottom: spacing.sm, borderBottomWidth: 1, borderBottomColor: RD.border },
  sectionTitle:     { fontSize: 15, fontWeight: '700', color: RD.text },

  // Form fields
  fieldGroup:       { marginBottom: spacing.base },
  label:            { fontSize: 13, fontWeight: '600', color: RD.textSec, marginBottom: 6, letterSpacing: 0.3 },
  inputWrap:        { flexDirection: 'row', alignItems: 'center', backgroundColor: RD.surface, borderRadius: 14, borderWidth: 1.5, borderColor: RD.borderStrong },
  inputIcon:        { marginLeft: 14 },
  input:            { flex: 1, paddingHorizontal: 13, paddingVertical: 15, fontSize: 15, color: RD.text, fontFamily: fonts.regular },
  helperText:       { fontSize: 13, color: RD.textMuted, marginBottom: spacing.md, lineHeight: 18 },

  // Slider
  sliderHeader:     { flexDirection: 'row', justifyContent: 'center', marginBottom: spacing.xs },
  sliderValue:      { fontSize: 28, fontWeight: '900', color: RD.maroon, fontFamily: fonts.heavy },
  slider:           { width: '100%', height: 40 },
  sliderLabels:     { flexDirection: 'row', justifyContent: 'space-between', marginTop: -4 },
  sliderLabel:      { fontSize: 12, color: RD.textFaint, fontWeight: '500' },

  // Map
  mapContainer:     { marginTop: spacing.base, borderRadius: 18, overflow: 'hidden', borderWidth: 1, borderColor: RD.border },

  // Save
  saveBtn:          { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: RD.ink, borderRadius: 16, height: 54, marginTop: spacing.md, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  saveBtnDisabled:  { opacity: 0.6 },
  saveBtnText:      { color: '#fff', fontSize: 16, fontWeight: '600', fontFamily: fonts.semiBold },

  bottomSpacer:     { height: 20 },
});
